import { Link, useLocation } from "react-router-dom";
import { LogOut, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { navigation } from "@/config/navigation";
import { WorkspaceSwitcher } from "@/components/layout/WorkspaceSwitcher";
import { SidebarLiveActivity } from "@/components/layout/SidebarLiveActivity";
import { WidgetErrorBoundary } from "@/components/WidgetErrorBoundary";
import { confirmLeaveDuringTransfer } from "@/lib/transferRunStore";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const location = useLocation();

  const isActive = (url: string) => {
    if (url === "/") return location.pathname === "/";
    return location.pathname.startsWith(url);
  };

  // Kreator "Przenies domeny" w trakcie przepisywania NS: BrowserRouter nie ma
  // useBlocker, wiec pytamy przed nawigacja (run i tak leci dalej w store).
  const guardNav = (e: React.MouseEvent) => {
    if (!confirmLeaveDuringTransfer()) e.preventDefault();
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" onClick={guardNav}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden">
                  <img
                    src="/logos/logo.png"
                    alt="PBNSD666 Logo"
                    className="size-8 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">PBNSD666</span>
                  <span className="truncate text-xs text-sidebar-foreground/60">
                    Panel zarzadzania
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navigation.map((group, groupIndex) => (
          <SidebarGroup key={groupIndex}>
            {group.label && (
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild={!item.disabled}
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                      disabled={item.disabled}
                      className={
                        item.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : undefined
                      }
                    >
                      {item.disabled ? (
                        <span>
                          <item.icon className="size-4" />
                          <span>{item.title}</span>
                        </span>
                      ) : (
                        <Link to={item.url} onClick={guardNav}>
                          <item.icon className="size-4" />
                          <span>{item.title}</span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        {/* SidebarLiveActivity (Kolejka workera) — ukryty, worker dziala w stub mode.
            Odkomentuj gdy worker bedzie w pelni dzialal. */}
        {false && (
          <WidgetErrorBoundary name="SidebarLiveActivity" fallback={null}>
            <SidebarLiveActivity />
          </WidgetErrorBoundary>
        )}
        <div className="px-1 pt-1 pb-0.5">
          <WorkspaceSwitcher />
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <span className="text-xs font-medium">
                      {user?.email?.charAt(0).toUpperCase() ?? "?"}
                    </span>
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      {user?.email ?? ""}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
                side="top"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut className="mr-2 size-4" />
                  Wyloguj
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
